import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Loader2, FileDown, ImageDown } from "lucide-react";
import { API, downloadFile, errMsg } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export function DownloadActions({ sim }) {
  const [busy, setBusy] = useState("");
  const dl = async (kind) => {
    setBusy(kind);
    try { await downloadFile(`/simulations/${sim.id}/${kind}`, `simulasi.${kind}`); } catch (e) { toast.error(errMsg(e, "Gagal membuat file")); } finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl border border-[#F28A19]/25 bg-[#F28A19]/5 p-4" data-testid="download-actions">
      <p className="text-sm font-medium text-[#17223B]">Apakah Anda ingin mendownload simulasi ini dalam format gambar atau PDF?</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={() => dl("pdf")} disabled={!!busy} className="bg-[#F28A19] hover:bg-[#DC7A0F]" data-testid="download-pdf-button">
          {busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}PDF A4
        </Button>
        <Button variant="outline" onClick={() => dl("png")} disabled={!!busy} data-testid="download-png-button">
          {busy === "png" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageDown className="mr-2 h-4 w-4" />}Gambar PNG
        </Button>
      </div>
    </div>
  );
}

export function AiSummary({ sim }) {
  const [model, setModel] = useState("gpt");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const history = sim.ai_summaries || [];

  const run = async () => {
    setBusy(true);
    setText("");
    try {
      const res = await fetch(`${API}/simulations/${sim.id}/ai-summary`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model }),
      });
      if (!res.ok) throw new Error("Gagal");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.delta) setText((t) => t + ev.delta);
          if (ev.error) toast.error(ev.error);
        }
      }
    } catch {
      toast.error("Ringkasan AI gagal dibuat. Coba lagi.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#5720B8]/15 p-4" data-testid="ai-summary-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-display text-sm font-bold text-[#351080]"><Sparkles className="h-4 w-4 text-[#5720B8]" />Ringkasan AI untuk Debitur</p>
          <p className="text-xs text-slate-500">Pesan siap kirim (WhatsApp) berdasarkan angka simulasi final — AI tidak menghitung ulang.</p>
        </div>
        <div className="flex gap-2">
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger className="w-[170px]" data-testid="ai-model-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="gpt">ChatGPT (GPT-5.6)</SelectItem><SelectItem value="gemini">Gemini 3.1 Pro</SelectItem></SelectContent>
          </Select>
          <Button onClick={run} disabled={busy} className="bg-[#5720B8] hover:bg-[#4a1b9c]" data-testid="ai-generate-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buat"}
          </Button>
        </div>
      </div>
      {(text || busy) && (
        <div className="mt-4 rounded-lg bg-[#F5F6FA] p-4 text-sm leading-relaxed whitespace-pre-wrap text-[#17223B]" data-testid="ai-summary-text">
          {text || "Menyusun ringkasan…"}
          {text && !busy && (
            <Button size="sm" variant="ghost" className="mt-2 text-[#5720B8]" onClick={() => { navigator.clipboard.writeText(text); toast.success("Disalin"); }} data-testid="ai-copy-button">
              <Copy className="mr-1.5 h-3.5 w-3.5" />Salin
            </Button>
          )}
        </div>
      )}
      {!text && history.length > 0 && (
        <details className="mt-3 text-sm"><summary className="cursor-pointer text-xs font-semibold text-[#5720B8]">Ringkasan sebelumnya ({history.length})</summary>
          {history.slice(-3).reverse().map((h) => <div key={h.id} className="mt-2 rounded-lg bg-[#F5F6FA] p-3 whitespace-pre-wrap"><p className="mb-1 text-[11px] text-slate-400">{h.model.toUpperCase()} • {h.user_name} • {fmtDateTime(h.created_at)}</p>{h.text}</div>)}
        </details>
      )}
    </div>
  );
}
